import React, { useState, useEffect } from 'react';
import { Button, Typography, Form, Select, useToast } from '@crisellevaldez/speculo-ui';
import { Save, List } from 'lucide-react';
import { Organization } from '../../../../types/organization.types';
import { getFubErrorMessage } from './utils/errorHelpers';

interface FubStagesConfigurationProps {
  selectedOrg?: Organization;
  isConfigured: boolean;
  hasCustomFields: boolean;
  hasStagesAllowlist?: boolean;
  pullFubStages: (orgId: string) => Promise<any>;
  getFubStageAllowlist: (orgId: string) => Promise<any>;
  setFubStageAllowlist: (
    orgId: string,
    allowedStages: string[],
    existingFubSetup?: any
  ) => Promise<any>;
  onStagesConfigured: () => void;
  isOpen?: boolean;
}

export const FubStagesConfiguration: React.FC<FubStagesConfigurationProps> = ({
  selectedOrg,
  isConfigured,
  hasCustomFields,
  hasStagesAllowlist = false,
  pullFubStages,
  getFubStageAllowlist,
  setFubStageAllowlist,
  onStagesConfigured,
  isOpen = false,
}) => {
  // State for stages
  const [stages, setStages] = useState<{ id: number; name: string }[]>([]);
  const [allowedStages, setAllowedStages] = useState<string[]>([]);
  const [selectedStages, setSelectedStages] = useState<string[]>([]);
  const [isLoadingStages, setIsLoadingStages] = useState(false);
  const [isSavingStages, setIsSavingStages] = useState(false);
  const [hasLoadedStages, setHasLoadedStages] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const { showToast } = useToast();

  // Load stages data
  const loadStagesData = async () => {
    if (!selectedOrg?.id) return;

    try {
      setLoadError(null);

      // Get stages
      const stagesResult = await pullFubStages(selectedOrg.id);
      if (stagesResult?.success) {
        setStages(stagesResult.stages);
      }

      // Get allowlist
      const allowlistResult = await getFubStageAllowlist(selectedOrg.id);
      if (allowlistResult?.success) {
        setAllowedStages(allowlistResult.allowedStages);
        setSelectedStages(allowlistResult.allowedStages);
      }

      setHasLoadedStages(true);
    } catch (error) {
      console.error('Error loading stages data:', error);
      const errorMsg = getFubErrorMessage(error, 'Failed to load stages data');
      setLoadError(errorMsg);
      showToast({
        message: errorMsg,
        variant: 'error',
      });
    } finally {
      setIsLoadingStages(false);
    }
  };

  // Trigger loading stages when the component becomes visible
  useEffect(() => {
    if (isOpen && !hasLoadedStages && !isLoadingStages && selectedOrg?.id) {
      // Set loading state to prevent multiple calls
      setIsLoadingStages(true);

      // Load stages data
      loadStagesData();
    }
  }, [isOpen, hasLoadedStages, isLoadingStages, selectedOrg?.id]);

  // Function to handle stage selection
  const handleStageSelection = (value: string | string[]) => {
    if (Array.isArray(value)) {
      setSelectedStages(value);
    } else {
      setSelectedStages([value]);
    }
  };

  // Function to save stage allowlist
  const handleSaveStageAllowlist = async () => {
    if (!selectedOrg?.id) return;

    try {
      setIsSavingStages(true);
      const result = await setFubStageAllowlist(
        selectedOrg.id,
        selectedStages,
        selectedOrg?.fubSetup
      );
      if (result?.success) {
        setAllowedStages(result.allowedStages);
        showToast({
          message: 'Stage allowlist updated successfully',
          variant: 'success',
        });

        // Notify parent component
        onStagesConfigured();
      } else {
        showToast({
          message: getFubErrorMessage(result, 'Failed to update stage allowlist'),
          variant: 'error',
        });
      }
    } catch (error) {
      console.error('Error saving stage allowlist:', error);
      showToast({
        message: getFubErrorMessage(error, 'Failed to save stage allowlist'),
        variant: 'error',
      });
    } finally {
      setIsSavingStages(false);
    }
  };

  // If API key is not configured or custom fields are not set up, don't show this component
  if (!isConfigured || !hasCustomFields) {
    return null;
  }

  return (
    <div>
      <div className="space-y-4">
        <Typography variant="p">
          Configure which stages in Follow Up Boss allow AI engagement. By default, only stages in
          the allowlist permit AI engagement with contacts.
        </Typography>

        {isLoadingStages ? (
          <div className="text-center py-4">
            <Typography variant="p" className="text-sm text-gray-500">
              Loading stages...
            </Typography>
          </div>
        ) : loadError ? (
          <div className="text-center py-4">
            <Typography variant="p" className="text-sm text-red-500">
              {loadError}
            </Typography>
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                setIsLoadingStages(true);
                loadStagesData();
              }}
              className="mt-2"
            >
              Retry
            </Button>
          </div>
        ) : (
          <>
            {stages.length > 0 ? (
              <>
                <Form.Control>
                  <Form.Label>Select Stages for AI Engagement</Form.Label>
                  <Select
                    multiple
                    value={selectedStages}
                    onChange={handleStageSelection}
                    className="w-full"
                    rows={5}
                    options={stages.map((stage) => ({
                      value: stage.name,
                      label: stage.name,
                    }))}
                  />
                  <Form.HelperText>Select multiple stages to allow AI engagement</Form.HelperText>
                </Form.Control>

                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    type="button"
                    onClick={handleSaveStageAllowlist}
                    disabled={isSavingStages}
                    isLoading={isSavingStages}
                    className="flex items-center gap-1 h-8"
                  >
                    <Save className="h-4 w-4" />
                    Save Stages
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <Typography variant="p" className="text-sm text-gray-500">
                  No stages found in Follow Up Boss.
                </Typography>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
